/**
 * Tiny, deliberately schematic examples for the question library.
 *
 * These are HTML/CSS rather than screenshots so they stay crisp, inherit the
 * application theme, and give new server-provided types a useful fallback.
 */
export function QuestionTypePreview({ type }: { type: string }) {
  return (
    <div className={`tc-type-preview tc-type-preview-${type}`} aria-hidden="true">
      <span className="tc-type-preview-number">1.</span>
      <PreviewContent type={type} />
    </div>
  )
}

function PreviewContent({ type }: { type: string }) {
  switch (type) {
    case 'missing_number':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Write the missing numbers.</MiniPrompt>
          <div className="tc-mini-number-row">5, 6, <Blank />, 8, <Blank /></div>
          <div className="tc-mini-number-row">12, <Blank />, 14, <Blank />, 16</div>
        </div>
      )
    case 'count_pictures':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Count the pictures.</MiniPrompt>
          <PictureCount symbol="●" count={4} />
          <PictureCount symbol="★" count={3} />
          <PictureCount symbol="◆" count={5} />
        </div>
      )
    case 'compare_sizes':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Circle the bigger object.</MiniPrompt>
          <div className="tc-mini-sizes"><span>●</span><span>●</span></div>
          <div className="tc-mini-divider" />
          <div className="tc-mini-sizes is-reversed"><span>◆</span><span>◆</span></div>
        </div>
      )
    case 'shape_properties':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Circle the correct letter.</MiniPrompt>
          <div className="tc-mini-circle-correct">
            <span className="tc-mini-circle-picture">🚌</span>
            <div>
              <span>B</span>
              <span>C</span>
              <span>A</span>
            </div>
          </div>
        </div>
      )
    case 'write_in_box':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Write the first letter.</MiniPrompt>
          <div className="tc-mini-word-complete"><span>➡️</span><b><i />rrow</b></div>
          <div className="tc-mini-word-complete"><span>🦅</span><b><i />alcon</b></div>
        </div>
      )
    case 'tick_cross_pictures':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Tick or cross each picture.</MiniPrompt>
          <div className="tc-mini-tick-cross">
            <span>🧺<i /></span>
            <span>⚽<i /></span>
          </div>
        </div>
      )
    case 'multiple_choice':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Which number is even?</MiniPrompt>
          <MiniChoices values={['3', '4', '5', '7']} />
        </div>
      )
    case 'short_answer':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Name the animal.</MiniPrompt>
          <div className="tc-mini-animal">♞</div>
          <div className="tc-mini-answer-line" />
        </div>
      )
    case 'alphabet_handwriting':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Trace and write.</MiniPrompt>
          <div className="tc-mini-handwriting"><b>A a</b><span>A a</span></div>
        </div>
      )
    case 'colouring':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Colour the picture.</MiniPrompt>
          <div className="tc-mini-colouring">☆</div>
        </div>
      )
    case 'drawing_response':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Draw a square.</MiniPrompt>
          <div className="tc-mini-drawing"><span>⌁</span></div>
        </div>
      )
    case 'fill_in_blanks':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Complete the sentence.</MiniPrompt>
          <div className="tc-mini-sentence">The <span /> is blue.</div>
          <div className="tc-mini-sentence">I can <span />.</div>
        </div>
      )
    case 'matching':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Match the pairs.</MiniPrompt>
          <div className="tc-mini-matching">
            <span>1</span><i /><span>A</span>
            <span>2</span><i /><span>B</span>
            <span>3</span><i /><span>C</span>
          </div>
        </div>
      )
    case 'numeric':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Write the answer.</MiniPrompt>
          <div className="tc-mini-equation">7 + 5 = <Blank /></div>
          <div className="tc-mini-equation">9 − 3 = <Blank /></div>
        </div>
      )
    case 'number_sequence_grid':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Write numbers 1–10.</MiniPrompt>
          <div className="tc-mini-grid">
            {Array.from({ length: 10 }, (_, index) => <span key={index}>{index < 2 ? index + 1 : ''}</span>)}
          </div>
        </div>
      )
    case 'odd_one_out':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Circle the odd one.</MiniPrompt>
          <div className="tc-mini-odd"><span>●</span><span>●</span><span>▲</span><span>●</span></div>
        </div>
      )
    case 'passage':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Read and answer.</MiniPrompt>
          <div className="tc-mini-passage"><span /><span /><span /></div>
          <div className="tc-mini-answer-line" />
          <div className="tc-mini-answer-line is-short" />
        </div>
      )
    case 'true_false':
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Plants need sunlight.</MiniPrompt>
          <div className="tc-mini-true-false"><span>○ True</span><span>○ False</span></div>
        </div>
      )
    default:
      return (
        <div className="tc-mini-stack">
          <MiniPrompt>Question example</MiniPrompt>
          <div className="tc-mini-passage"><span /><span /></div>
          <div className="tc-mini-answer-line" />
        </div>
      )
  }
}

function MiniPrompt({ children }: { children: string }) {
  return <strong className="tc-mini-prompt">{children}</strong>
}

function Blank() {
  return <span className="tc-mini-blank" />
}

function PictureCount({ symbol, count }: { symbol: string; count: number }) {
  return (
    <div className="tc-mini-picture-count">
      <span>{Array.from({ length: count }, () => symbol).join(' ')}</span>
      <Blank />
    </div>
  )
}

function MiniChoices({ values }: { values: string[] }) {
  return (
    <div className="tc-mini-choices">
      {values.map((value, index) => (
        <span key={value}><i>{String.fromCharCode(65 + index)}</i>{value}</span>
      ))}
    </div>
  )
}
